import { SDK_VERSION } from '@sentry/browser';
import {
  AnalyticsEvent,
  ButtonComponentV2,
  ProcessTypeInput,
  TabaPayChallengeAuthenticateInput,
  TabaPayChallengeAuthenticateResultTypenames,
  TabaPayChallengeInitInput,
  TabaPayChallengeInitResultTypenames,
  TabaPayChallengeLookupInput,
  TabaPayChallengeLookupResultTypenames,
} from '@varo/graphql-schema-types';
import {
  CHALLENGE_TIMEOUT,
  TABAPAY_ENV,
  TABAPAY_NATIVE_TIMEOUT,
  UI_TYPE,
} from 'features/moveMoney/constants/constants';
import useCancelTabapayTransfer from 'features/moveMoney/screens/dcf/hooks/useCancelTabapayTransfer';
import { useTabaPayChallengeAuthenticate } from 'features/moveMoney/screens/dcf/hooks/useTabaPayChallengeAuthenticate';
import { useTabaPayChallengeInit } from 'features/moveMoney/screens/dcf/hooks/useTabaPayChallengeInit';
import { useTabaPayChallengeLookup } from 'features/moveMoney/screens/dcf/hooks/useTabaPayChallengeLookup';
import {
  getTabaPayEnv,
  handle3dsActionCode,
  logDebitCardCheckFailedEvents,
  logDebitCardCheckPassedEvents,
  logDebitCardCheckStepCompletedEvents,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import {
  getTabapayErrorScreen,
  getTabapayPartialAuthErrorScreen,
  loadExpoTabapay,
} from 'features/myVaro/screens/ExternalAccounts/helper/TabapayHelper';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAndroid, isWeb } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import theme from 'shared/styles/theme';
import { logError } from 'shared/utils/sentry/sentry';

export const useThreeDsHandler = () => {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'paymentDetails',
  });
  const { t: translatedTextMyVaro } = useTranslation('myVaro');
  const { triggerChallengeInit } = useTabaPayChallengeInit();
  const { cancelTabapayTransfer } = useCancelTabapayTransfer();
  const { triggerChallengeLookup } = useTabaPayChallengeLookup();
  const { triggerAuthenticate } = useTabaPayChallengeAuthenticate();
  const { segmentClient } = useAnalyticsConfiguration();
  const { currentEnv } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  const [isLoading, setIsLoading] = useState(false);
  const [initTransactionId, setInitTransactionId] = useState<string>(''); /// This is used for cancel transaction mutation
  const [errorScreenThreeDs, setErrorScreen] = useState<{
    screenType: ErrorScreenType.Error;
    messageHeadline: string;
    messageBody: string;
    primaryButton?: ButtonComponentV2;
    secondaryButton?: ButtonComponentV2;
    is3DSError?: boolean;
    processType?: string;
    analytics?: AnalyticsEvent;
    isUserCancelled3DS?: boolean;
    isPartialAuth?: boolean;
    approvedAmount?: string;
    statusCode?: string;
  } | null>(null);
  const radius = isAndroid ? 16 : 4;
  let initResultID: string;
  const handleThreeDS = async (
    processType: ProcessTypeInput,
    flowName: string,
    externalCardId?,
    sourceTransactionId?: string,
  ) => {
    setIsLoading(true);
    const ExpoTabapay = await loadExpoTabapay();

    const handleAuthenticate = async (
      ds: string,
      jwt: string,
      transaction: string,
    ) => {
      if (!ds || !jwt || !transaction) {
        setIsLoading(false);
        throw new Error('Missing required parameters for authentication');
      }
      const authenticateInput: TabaPayChallengeAuthenticateInput = {
        dsId: ds,
        jwtToken: jwt,
        transactionId: transaction,
        processType: processType,
      };
      const result = await triggerAuthenticate(authenticateInput);
      const errorScreen = getTabapayErrorScreen(result);
      if (errorScreen) {
        setIsLoading(false);
        setErrorScreen(errorScreen);
        logDebitCardCheckFailedEvents(
          flowName,
          errorScreen.code,
          errorScreen.messageBody,
          '3DS AUTH',
          segmentClient,
        );
      }

      const partialAuthErrorScreen = getTabapayPartialAuthErrorScreen(
        result,
        translatedTextMyVaro,
      );

      if (partialAuthErrorScreen) {
        setIsLoading(false);
        setErrorScreen(partialAuthErrorScreen);
      }

      if (
        result.__typename ===
        TabaPayChallengeAuthenticateResultTypenames.TabaPayChallengeAuthenticateResultError
      ) {
        setIsLoading(false);
        setErrorScreen({
          screenType: ErrorScreenType.Error,
          messageHeadline: result.title,
          messageBody: result.message,
        });
        logDebitCardCheckFailedEvents(
          flowName,
          result.code,
          result.message,
          '3DS AUTH',
          segmentClient,
        );
      }
      setIsLoading(false);
      if (
        result.__typename ===
        TabaPayChallengeAuthenticateResultTypenames.TabaPayChallengeAuthenticateResultSuccess
      ) {
        logDebitCardCheckPassedEvents('3DS AUTH', segmentClient, flowName);
      }
      return (
        result.__typename ===
        TabaPayChallengeAuthenticateResultTypenames.TabaPayChallengeAuthenticateResultSuccess
      );
    };

    const handleInit = async () => {
      setErrorScreen(null);
      let cardIdentifier: Partial<TabaPayChallengeInitInput> = {};
      if (externalCardId) {
        cardIdentifier = { externalCardID: `${externalCardId}` };
      } else if (sourceTransactionId) {
        cardIdentifier = { transactionId: `${sourceTransactionId}` };
      }

      const initInput: TabaPayChallengeInitInput = {
        sdkVersion: SDK_VERSION,
        processType: processType,
        ...cardIdentifier,
      };
      const initResult = await triggerChallengeInit(initInput);
      if (
        initResult.__typename ===
        TabaPayChallengeInitResultTypenames.TabaPayChallengeInitResultError
      ) {
        setErrorScreen({
          screenType: ErrorScreenType.Error,
          messageHeadline: initResult.title,
          messageBody: initResult.message,
        });
        setIsLoading(false);
        logDebitCardCheckFailedEvents(
          flowName,
          initResult.code,
          initResult.message,
          '3DS INIT',
          segmentClient,
        );
      }

      const errorScreen = getTabapayErrorScreen(initResult);
      if (errorScreen) {
        setIsLoading(false);
        setErrorScreen(errorScreen);
        logDebitCardCheckFailedEvents(
          flowName,
          errorScreen.code,
          errorScreen.messageBody,
          '3DS INIT',
          segmentClient,
        );
      }

      if (
        initResult.__typename ===
        TabaPayChallengeInitResultTypenames.TabaPayChallengeInitResultSuccess
      ) {
        logDebitCardCheckPassedEvents('3DS INIT', segmentClient, flowName);
        const localDsId = initResult.dsId;
        const localJwt = initResult.jwtToken;
        const localTransactionID = initResult.transactionID;
        const isChallenge = initResult.challengeRequired;
        setInitTransactionId(localTransactionID);
        initResultID = localTransactionID;
        return { localDsId, localJwt, localTransactionID, isChallenge };
      }
    };

    function isValidTabaPayEnv(value: string): value is TABAPAY_ENV {
      return value === 'staging' || value === 'production';
    }
    const tabaPayEnv = getTabaPayEnv(currentEnv);
    if (!isValidTabaPayEnv(tabaPayEnv)) {
      throw new Error(`Invalid TabaPayEnv: ${tabaPayEnv}`);
    }

    try {
      await ExpoTabapay.setup3DS(
        tabaPayEnv,
        TABAPAY_NATIVE_TIMEOUT,
        CHALLENGE_TIMEOUT,
        UI_TYPE,
        {
          buttons: {
            verify: {
              backgroundColor: theme.color.interactive.primary,
              textColor: theme.color.text.inverse,
              cornerRadius: radius,
              textFontName: 'Helvetica',
              textFontSize: 14,
            },
            resend: {
              textColor: theme.color.text.inverse,
              cornerRadius: radius,
              textFontName: 'Helvetica',
              textFontSize: 14,
            },
          },
          toolbar: {
            backgroundColor: theme.color.background.primary,
            buttonText: translatedText('tabapayScreen.back'),
            headerText: translatedText('tabapayScreen.title'),
          },
          textBox: {
            borderColor: theme.color.interactive.primary,
            cornerRadius: radius,
          },
        },
      );
      const handleInitResult = await handleInit();
      if (!handleInitResult?.isChallenge) {
        return true;
      }
      if (
        !handleInitResult?.localJwt &&
        !handleInitResult?.localDsId &&
        !handleInitResult?.localTransactionID
      ) {
        setIsLoading(false);
        throw new Error('JWT returned empty');
      }

      logDebitCardCheckStepCompletedEvents(
        'sdk_setup_complete',
        flowName,
        segmentClient,
      );

      const sessionId = await ExpoTabapay.initiate3DS(
        handleInitResult.localJwt,
      );
      logDebitCardCheckStepCompletedEvents(
        '3ds_sdk_initiated',
        flowName,
        segmentClient,
      );

      const lookupInput: TabaPayChallengeLookupInput = {
        dsId: handleInitResult.localDsId,
        sdkSessionID: sessionId,
        isBrowser: isWeb,
        transactionId: handleInitResult.localTransactionID,
        processType: processType,
      };
      const lookupResult = await triggerChallengeLookup(lookupInput);
      //If no payload in lookup response return true
      //  IF PAYLOAD PRESENT DO THE startChallengeAndValidate AND THEN RUN TABAPAYCHALLENGEAUTHENTICATE
      const errorScreen = getTabapayErrorScreen(lookupResult);
      if (errorScreen) {
        setIsLoading(false);
        setErrorScreen(errorScreen);
        logDebitCardCheckFailedEvents(
          flowName,
          errorScreen.code,
          errorScreen.messageBody,
          '3DS LOOKUP',
          segmentClient,
        );
      }

      const partialAuthErrorScreen = getTabapayPartialAuthErrorScreen(
        lookupResult,
        translatedTextMyVaro,
      );

      if (partialAuthErrorScreen) {
        setIsLoading(false);
        setErrorScreen(partialAuthErrorScreen);
      }

      if (
        lookupResult.__typename ===
        TabaPayChallengeLookupResultTypenames.TabaPayChallengeLookupResultError
      ) {
        setErrorScreen({
          screenType: ErrorScreenType.Error,
          messageHeadline: lookupResult.title,
          messageBody: lookupResult.message,
        });
        setIsLoading(false);
        logDebitCardCheckFailedEvents(
          flowName,
          lookupResult.code,
          lookupResult.message,
          '3DS LOOKUP',
          segmentClient,
        );
      }

      if (
        lookupResult.__typename ===
          TabaPayChallengeLookupResultTypenames.TabaPayChallengeLookupResultSuccess &&
        !lookupResult.payload
      ) {
        logDebitCardCheckPassedEvents('3DS LOOKUP', segmentClient, flowName);
        setIsLoading(false);
        return true;
      }

      if (
        lookupResult.__typename ===
        TabaPayChallengeLookupResultTypenames.TabaPayChallengeLookupResultSuccess
      ) {
        logDebitCardCheckPassedEvents('3DS LOOKUP', segmentClient, flowName);
        if (!lookupResult?.processorTransactionId || !lookupResult.payload) {
          setIsLoading(false);
          throw new Error('Missing data to call startChallengeAndValidate');
        }

        const challengeResponse = await ExpoTabapay.startChallengeAndValidate(
          lookupResult.processorTransactionId,
          lookupResult.payload,
        );
        logDebitCardCheckStepCompletedEvents(
          'challenge_start',
          flowName,
          segmentClient,
          '3DS LOOKUP',
        );

        let result = false;
        if (
          processType !== ProcessTypeInput.Transaction &&
          challengeResponse.actionCode === 'CANCEL'
        ) {
          void cancelTabapayTransfer(
            {
              transactionId: initResultID,
            },
            flowName,
          );
        }
        try {
          await handle3dsActionCode({
            actionCode: challengeResponse.actionCode,
            setErrorScreen,
            processType,
            flowName,
            translatedText,
            segmentClient,
            errorDescription: challengeResponse.errorDescription,
            errorNumber: challengeResponse.errorNumber,
            onSuccess: async () => {
              logDebitCardCheckStepCompletedEvents(
                'challenge_validated',
                flowName,
                segmentClient,
                '3DS LOOKUP',
              );

              logDebitCardCheckPassedEvents(
                '3DS LOOKUP',
                segmentClient,
                flowName,
              );
              result = await handleAuthenticate(
                handleInitResult.localDsId,
                challengeResponse.serverJWT,
                handleInitResult.localTransactionID,
              );
            },
            setIsLoading,
            resolve: (response) => {
              if (!response) {
                setIsLoading(false);
              }
            },
          });
        } catch (error) {
          logError(error);
        }

        return result;
      }
    } catch (error) {
      setIsLoading(false);
      logError(error);
    }
  };

  const resetError = useCallback(() => {
    setErrorScreen(null);
  }, []);

  return {
    handleThreeDS,
    errorScreenThreeDs,
    isLoading,
    initTransactionId,
    resetError,
  };
};
